import React, { useEffect, useState, useRef, useMemo } from "react";
import { Link, useNavigate, useParams, Navigate } from "react-router-dom";
import { api, imageUrl, errorMessage, formatRupiah, formatDiscount } from "../lib/api";
import { STATUS_OPTIONS } from "../lib/status";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Save, Upload, Loader2, Image as ImageIcon, Trash2, Plus } from "lucide-react";

export default function ProductForm() {
    const { id } = useParams();
    const isEdit = !!id;
    const { user } = useAuth();
    const navigate = useNavigate();
    const fileRef = useRef(null);

    const [form, setForm] = useState({
        name: "",
        description: "",
        list_price: "",
        discount_percent: "",
        price: "",
        sizes: "",
        sku: "",
        category: "",
        brand: "",
        keywords: "",
        status: "ready",
        stock_qty: 0,
    });
    const [variants, setVariants] = useState([]); // [{varian, sub_varian, price, list_price, discount_percent}]
    const [imagePath, setImagePath] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        // Load suggestion lists for datalist
        (async () => {
            try {
                const [{ data: bs }, { data: cs }] = await Promise.all([
                    api.get("/products/brands"),
                    api.get("/products/categories"),
                ]);
                setBrands(bs || []);
                setCategories(cs || []);
            } catch { /* ignore */ }
        })();
    }, []);

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const { data } = await api.get(`/products/${id}`);
                setForm({
                    name: data.name || "",
                    description: data.description || "",
                    list_price: data.list_price ?? "",
                    discount_percent: data.discount_percent ?? "",
                    price: data.price ?? "",
                    sizes: (data.sizes || []).join(", "),
                    sku: data.sku || "",
                    category: data.category || "",
                    brand: data.brand || "",
                    keywords: data.keywords || "",
                    status: data.status || "ready",
                    stock_qty: data.stock_qty || 0,
                });
                setVariants((data.variants || []).map((v) => ({
                    varian: v.varian || "",
                    sub_varian: v.sub_varian || "",
                    price: v.price ?? "",
                    list_price: v.list_price ?? "",
                    discount_percent: v.discount_percent ?? "",
                })));
                setImagePath(data.image_path || null);
            } catch (e) {
                setError(errorMessage(e));
            } finally {
                setLoading(false);
            }
        })();
    }, [id, isEdit]);

    // Live-derive missing pricing field (visual hint only — backend also recomputes on import).
    const derived = useMemo(() => {
        const list = Number(form.list_price) || 0;
        const disc = Number(form.discount_percent) || 0;
        const net = Number(form.price) || 0;
        let computedNet = net;
        let computedDisc = disc;
        if (list > 0 && disc > 0 && net === 0) computedNet = Math.round(list * (1 - disc / 100));
        if (list > 0 && net > 0 && disc === 0 && net < list) {
            computedDisc = Math.round((1 - net / list) * 10000) / 100;
        }
        return { computedNet, computedDisc };
    }, [form.list_price, form.discount_percent, form.price]);

    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const addVariant = () => setVariants((v) => [...v, { varian: "", sub_varian: "", price: "", list_price: "", discount_percent: "" }]);
    const removeVariant = (idx) => setVariants((v) => v.filter((_, i) => i !== idx));
    const updateVariant = (idx, key, val) =>
        setVariants((v) => v.map((row, i) => (i === idx ? { ...row, [key]: val } : row)));

    const handleImagePick = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setImageFile(f);
        setImagePreview(URL.createObjectURL(f));
    };

    const removeNewImage = () => {
        setImageFile(null);
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImagePreview(null);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            // Resolve final pricing
            const list = Number(form.list_price) || 0;
            const disc = Number(form.discount_percent) || 0;
            let net = Number(form.price) || 0;
            if (net === 0 && list > 0 && disc > 0) net = Math.round(list * (1 - disc / 100));
            const finalList = list > 0 ? list : net;
            let finalDisc = disc;
            if (disc === 0 && finalList > net && net > 0) {
                finalDisc = Math.round((1 - net / finalList) * 1000) / 10;
            }

            const cleanedVariants = variants
                .map((v) => ({
                    varian: (v.varian || "").trim(),
                    sub_varian: (v.sub_varian || "").trim(),
                    price: Number(v.price) || 0,
                    list_price: Number(v.list_price) || 0,
                    discount_percent: Number(v.discount_percent) || 0,
                }))
                .filter((v) => v.varian || v.sub_varian || v.price > 0 || v.list_price > 0);

            // If product has variants and no top-level price was entered, default to min variant price.
            if (net === 0 && cleanedVariants.length > 0) {
                const vp = cleanedVariants.map((v) => v.price).filter((p) => p > 0);
                if (vp.length > 0) net = Math.min(...vp);
            }
            const adjustedFinalList = list > 0 ? list : net;

            const payload = {
                name: form.name.trim(),
                description: form.description,
                price: net,
                list_price: adjustedFinalList,
                discount_percent: Math.max(0, finalDisc),
                sizes: form.sizes.split(/[,;]/).map((s) => s.trim()).filter(Boolean),
                sku: form.sku,
                category: form.category,
                brand: form.brand,
                keywords: form.keywords,
                status: form.status || "ready",
                stock_qty: Number(form.stock_qty) || 0,
                variants: cleanedVariants,
            };
            let productId = id;
            if (isEdit) {
                await api.put(`/admin/products/${id}`, payload);
            } else {
                const { data } = await api.post("/admin/products", payload);
                productId = data.id;
            }

            if (imageFile) {
                const fd = new FormData();
                fd.append("file", imageFile);
                await api.post(`/admin/products/${productId}/image`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }

            navigate("/admin");
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <Loader2 className="animate-spin text-primary" size={22} />
            </div>
        );
    }

    // product_admin cannot create new products
    if (!isEdit && user && user.role !== "super_admin") {
        return <Navigate to="/admin" replace />;
    }

    return (
        <div data-testid="product-form-page" className="mx-auto max-w-3xl px-5 py-8">
            <Link
                to="/admin"
                data-testid="back-to-admin-link"
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
                <ArrowLeft size={14} /> Kembali ke Dashboard
            </Link>
            <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight">
                {isEdit ? "Edit Produk" : "Tambah Produk Baru"}
            </h1>

            <form onSubmit={onSubmit} className="mt-8 grid gap-6 md:grid-cols-[1fr_240px]">
                <div className="space-y-5">
                    <Field label="Nama Produk" required>
                        <input
                            data-testid="form-name-input"
                            value={form.name}
                            onChange={onChange("name")}
                            required
                            className="input"
                        />
                    </Field>
                    <Field label="Deskripsi">
                        <textarea
                            data-testid="form-description-input"
                            value={form.description}
                            onChange={onChange("description")}
                            rows={5}
                            className="input resize-y"
                        />
                    </Field>
                    <Field label="Kata Kunci (AI search) — opsional">
                        <textarea
                            data-testid="form-keywords-input"
                            value={form.keywords}
                            onChange={onChange("keywords")}
                            rows={2}
                            placeholder="60429, alexindo, ykk, m sekrup, 9K-86021"
                            className="input resize-y"
                        />
                        <p className="mt-1 text-xs text-muted-foreground">
                            Alias / sinonim / kode lain untuk produk ini — <strong>tidak tampil ke customer</strong>, hanya dipakai chatbot WhatsApp untuk match pertanyaan. Pisahkan dengan koma atau spasi.
                        </p>
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <Field label="Harga Liat (Rp)">
                            <input
                                data-testid="form-list-price-input"
                                type="number"
                                min="0"
                                step="1"
                                value={form.list_price}
                                onChange={onChange("list_price")}
                                className="input"
                                placeholder="100000"
                            />
                        </Field>
                        <Field label="Diskon (%)">
                            <input
                                data-testid="form-discount-input"
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={form.discount_percent}
                                onChange={onChange("discount_percent")}
                                className="input"
                                placeholder="25,00"
                            />
                        </Field>
                        <Field label="Harga Netto (Rp)">
                            <input
                                data-testid="form-price-input"
                                type="number"
                                min="0"
                                step="1"
                                value={form.price}
                                onChange={onChange("price")}
                                className="input"
                                placeholder="75000"
                            />
                        </Field>
                    </div>
                    <p className="-mt-2 text-xs text-muted-foreground">
                        Harga netto = harga dasar produk. Kalau ada varian, harga per varian akan dipakai dan harga ini jadi "mulai dari".
                        {(derived.computedNet > 0 || derived.computedDisc > 0) && (
                            <span className="ml-1 text-foreground">
                                Preview: <strong>{formatRupiah(derived.computedNet || Number(form.price) || 0)}</strong>
                                {derived.computedDisc > 0 && <> ({formatDiscount(derived.computedDisc)}% off)</>}
                            </span>
                        )}
                    </p>

                    <div className="grid grid-cols-3 gap-4">
                        <Field label="SKU">
                            <input
                                data-testid="form-sku-input"
                                value={form.sku}
                                onChange={onChange("sku")}
                                className="input"
                            />
                        </Field>
                        <Field label="Kategori">
                            <input
                                data-testid="form-category-input"
                                value={form.category}
                                onChange={onChange("category")}
                                className="input"
                                placeholder="Handle / Pintu / Kunci"
                                list="category-suggestions"
                            />
                            <datalist id="category-suggestions">
                                {(categories || []).map((c) => <option key={c} value={c} />)}
                            </datalist>
                        </Field>
                        <Field label="Merk / Brand">
                            <input
                                data-testid="form-brand-input"
                                value={form.brand}
                                onChange={onChange("brand")}
                                className="input"
                                placeholder="Dekkson / Dorma / Orchad"
                                list="brand-suggestions"
                            />
                            <datalist id="brand-suggestions">
                                {(brands || []).map((b) => <option key={b} value={b} />)}
                            </datalist>
                        </Field>
                    </div>

                    {/* Variants editor */}
                    <div className="rounded-lg border border-accent/30 bg-accent/5 p-4 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                            <div>
                                <span className="label-eyebrow text-accent">Varian & Sub Varian</span>
                                <p className="text-xs text-muted-foreground">
                                    Tambahkan varian (mis. warna, motif) + sub varian (mis. ukuran) beserta harganya. Kosongkan kalau produk tidak punya varian.
                                </p>
                            </div>
                            <button
                                type="button"
                                data-testid="add-variant-btn"
                                onClick={addVariant}
                                className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1.5 text-xs font-medium text-accent-foreground hover:opacity-90"
                            >
                                <Plus size={12} strokeWidth={2} /> Tambah Varian
                            </button>
                        </div>
                        {variants.length === 0 ? (
                            <p className="text-xs italic text-muted-foreground py-2 text-center" data-testid="variants-empty">
                                Belum ada varian. Klik "Tambah Varian" untuk menambahkan.
                            </p>
                        ) : (
                            <div className="space-y-2" data-testid="variants-list">
                                <div className="hidden sm:grid grid-cols-[minmax(110px,1fr)_minmax(110px,1fr)_110px_80px_110px_36px] gap-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                    <span>Varian</span>
                                    <span>Sub Varian</span>
                                    <span className="text-right">Harga Liat</span>
                                    <span className="text-right">Diskon %</span>
                                    <span className="text-right">Harga Netto</span>
                                    <span></span>
                                </div>
                                {variants.map((v, i) => (
                                    <div
                                        key={i}
                                        data-testid={`variant-row-${i}`}
                                        className="grid grid-cols-2 sm:grid-cols-[minmax(110px,1fr)_minmax(110px,1fr)_110px_80px_110px_36px] gap-2 items-start"
                                    >
                                        <input
                                            data-testid={`variant-name-${i}`}
                                            value={v.varian}
                                            onChange={(e) => updateVariant(i, "varian", e.target.value)}
                                            placeholder="Mis. Warna Merah"
                                            className="input col-span-2 sm:col-span-1"
                                        />
                                        <input
                                            data-testid={`variant-subname-${i}`}
                                            value={v.sub_varian}
                                            onChange={(e) => updateVariant(i, "sub_varian", e.target.value)}
                                            placeholder="Mis. Size M"
                                            className="input col-span-2 sm:col-span-1"
                                        />
                                        <input
                                            data-testid={`variant-list-price-${i}`}
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={v.list_price}
                                            onChange={(e) => updateVariant(i, "list_price", e.target.value)}
                                            placeholder="100000"
                                            title="Harga coret / liat"
                                            className="input text-right"
                                        />
                                        <input
                                            data-testid={`variant-discount-${i}`}
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            value={v.discount_percent}
                                            onChange={(e) => updateVariant(i, "discount_percent", e.target.value)}
                                            placeholder="0,00"
                                            title="Diskon persen (max 2 desimal)"
                                            className="input text-right"
                                        />
                                        <input
                                            data-testid={`variant-price-${i}`}
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={v.price}
                                            onChange={(e) => updateVariant(i, "price", e.target.value)}
                                            placeholder="0"
                                            title="Harga netto (yang dibayar customer)"
                                            className="input text-right"
                                        />
                                        <button
                                            type="button"
                                            data-testid={`variant-remove-${i}`}
                                            onClick={() => removeVariant(i)}
                                            title="Hapus varian"
                                            className="grid h-9 w-9 place-items-center rounded-md text-destructive hover:bg-destructive/10 col-span-2 sm:col-span-1 justify-self-end sm:justify-self-auto"
                                        >
                                            <Trash2 size={15} strokeWidth={1.75} />
                                        </button>
                                    </div>
                                ))}
                                <p className="text-[11px] text-muted-foreground pt-1">
                                    Tip: cukup isi 2 dari 3 kolom harga (Harga Liat, Diskon, Harga Netto) — yang ketiga dihitung otomatis.
                                </p>
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Status Stok" required>
                            <select
                                data-testid="form-status-select"
                                value={form.status}
                                onChange={onChange("status")}
                                className="input"
                            >
                                {STATUS_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Jumlah Stok (Qty)">
                            <input
                                data-testid="form-stock-input"
                                type="number"
                                min="0"
                                step="1"
                                value={form.stock_qty}
                                onChange={onChange("stock_qty")}
                                placeholder="0"
                                className="input"
                            />
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                        <Field label="Ukuran (pisahkan dengan koma)">
                            <input
                                data-testid="form-sizes-input"
                                value={form.sizes}
                                onChange={onChange("sizes")}
                                placeholder="S, M, L atau 350ml, 500ml"
                                className="input"
                            />
                        </Field>
                    </div>

                    {error && (
                        <div
                            data-testid="form-error"
                            className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                        >
                            {error}
                        </div>
                    )}

                    <div className="flex gap-2 pt-2">
                        <button
                            type="submit"
                            data-testid="form-submit-btn"
                            disabled={saving}
                            className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50 transition-all duration-200"
                        >
                            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} strokeWidth={1.75} />}
                            {isEdit ? "Simpan Perubahan" : "Buat Produk"}
                        </button>
                        <Link
                            to="/admin"
                            data-testid="form-cancel-link"
                            className="inline-flex items-center rounded-full border border-border bg-white px-5 py-2.5 text-sm hover:bg-secondary transition-all duration-200"
                        >
                            Batal
                        </Link>
                    </div>
                </div>

                <div className="space-y-3">
                    <span className="label-eyebrow text-muted-foreground">Gambar Produk</span>
                    <div className="aspect-square overflow-hidden rounded-lg border border-border bg-[#FDFBF7] grid place-items-center p-3">
                        {imagePreview ? (
                            <img src={imagePreview} alt="preview" className="h-full w-full object-contain" data-testid="image-preview-new" />
                        ) : imagePath ? (
                            <img src={imageUrl(imagePath)} alt="produk" className="h-full w-full object-contain" data-testid="image-preview-existing" />
                        ) : (
                            <div className="text-muted-foreground text-center">
                                <ImageIcon strokeWidth={1.25} size={36} className="mx-auto" />
                                <p className="mt-2 text-xs">Belum ada gambar</p>
                            </div>
                        )}
                    </div>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImagePick}
                        data-testid="image-file-input"
                    />
                    <div className="flex gap-2">
                        <button
                            type="button"
                            data-testid="image-upload-btn"
                            onClick={() => fileRef.current?.click()}
                            className="flex-1 inline-flex items-center justify-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-sm hover:bg-secondary transition-all duration-200"
                        >
                            <Upload size={14} strokeWidth={1.75} /> Pilih
                        </button>
                        {imagePreview && (
                            <button
                                type="button"
                                data-testid="image-remove-btn"
                                onClick={removeNewImage}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-white text-destructive hover:bg-destructive/10 transition-all duration-200"
                                title="Buang"
                            >
                                <Trash2 size={14} strokeWidth={1.75} />
                            </button>
                        )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Otomatis dikonversi ke JPEG 1200×1200 berkualitas tinggi (aspect ratio dipertahankan).
                    </p>
                </div>

                {isEdit && (
                    <GallerySection productId={id} />
                )}
            </form>

            <style>{`
                .input {
                    width: 100%;
                    border-radius: var(--radius);
                    border: 1px solid hsl(var(--border));
                    background: #fff;
                    padding: 0.625rem 0.875rem;
                    font-size: 0.875rem;
                    outline: none;
                    transition: border-color 200ms ease;
                }
                .input:focus { border-color: hsl(var(--primary)); }
            `}</style>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="label-eyebrow text-muted-foreground">
                {label} {required && <span className="text-accent">*</span>}
            </span>
            <div className="mt-2">{children}</div>
        </label>
    );
}

const GALLERY_TYPES = [
    { value: "installed", label: "Terpasang", desc: "Foto produk sudah terpasang di lokasi" },
    { value: "detail", label: "Detail / Close-up", desc: "Tekstur, finishing, bagian penting" },
    { value: "diagram", label: "Diagram / Ukuran", desc: "Drawing teknis atau dimensi" },
];

function GallerySection({ productId }) {
    const [gallery, setGallery] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [uploading, setUploading] = React.useState(false);
    const [newType, setNewType] = React.useState("installed");
    const [newCaption, setNewCaption] = React.useState("");
    const fileRef = React.useRef(null);

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/products/${productId}`);
            setGallery(data.gallery || []);
        } catch { /* ignore */ }
        setLoading(false);
    }, [productId]);

    React.useEffect(() => { load(); }, [load]);

    const handleUpload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (gallery.length >= 3) {
            alert("Maks 3 foto gallery per produk");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            fd.append("type", newType);
            fd.append("caption", newCaption);
            await api.post(`/admin/products/${productId}/gallery`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setNewCaption("");
            if (fileRef.current) fileRef.current.value = "";
            await load();
        } catch (err) {
            alert(errorMessage(err));
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (photoId) => {
        if (!window.confirm("Hapus foto ini?")) return;
        try {
            await api.delete(`/admin/products/${productId}/gallery/${photoId}`);
            await load();
        } catch (err) {
            alert(errorMessage(err));
        }
    };

    return (
        <div data-testid="gallery-section" className="space-y-4 rounded-xl border border-border bg-white p-5 sm:p-6">
            <div>
                <h3 className="text-base font-semibold">Foto Tambahan (Gallery)</h3>
                <p className="text-xs text-muted-foreground mt-1">
                    Maks 3 foto. Customer bisa minta lewat WA: "foto terpasang?", "detail finishing?", "ukurannya?"
                </p>
            </div>

            {/* Existing photos */}
            {!loading && gallery.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="gallery-list">
                    {gallery.map((g) => (
                        <div key={g.id} className="relative rounded-lg border border-border bg-secondary/30 overflow-hidden">
                            <img src={imageUrl(g.path)} alt={g.caption || g.type} className="w-full aspect-square object-cover" />
                            <div className="p-2 space-y-1">
                                <div className="flex items-center justify-between">
                                    <span className="label-eyebrow text-primary text-[10px]">
                                        {GALLERY_TYPES.find((t) => t.value === g.type)?.label || g.type}
                                    </span>
                                    <button
                                        type="button"
                                        data-testid={`gallery-delete-${g.id}`}
                                        onClick={() => handleDelete(g.id)}
                                        className="inline-flex h-6 w-6 items-center justify-center rounded text-destructive hover:bg-destructive/10"
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                                {g.caption && <p className="text-[11px] text-muted-foreground line-clamp-2">{g.caption}</p>}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add new */}
            {gallery.length < 3 && (
                <div className="rounded-lg border border-dashed border-border bg-secondary/20 p-4 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Jenis Foto">
                            <select
                                data-testid="gallery-type-select"
                                value={newType}
                                onChange={(e) => setNewType(e.target.value)}
                                className="input"
                            >
                                {GALLERY_TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label} — {t.desc}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Caption (opsional)">
                            <input
                                data-testid="gallery-caption-input"
                                value={newCaption}
                                onChange={(e) => setNewCaption(e.target.value)}
                                placeholder="mis. Terpasang di pintu kayu mahogani"
                                className="input"
                                maxLength={200}
                            />
                        </Field>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            data-testid="gallery-file-input"
                            onChange={handleUpload}
                        />
                        <button
                            type="button"
                            data-testid="gallery-upload-btn"
                            disabled={uploading}
                            onClick={() => fileRef.current?.click()}
                            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:opacity-90 disabled:opacity-50"
                        >
                            {uploading ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
                            {uploading ? "Upload..." : `Tambah Foto (${gallery.length}/3)`}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
